import React, { useCallback, useEffect, useRef, useState } from 'react';

import { IconChevronDown, IconChevronRight } from '../Icon';
import Notification from '../Notification';
import MarketLogo from './MarketLogo';
import {
  Body,
  Footer,
  Group,
  GroupBtn,
  GroupBtnIcon,
  GroupItem,
  GroupLink,
  GroupList,
  Item,
  LinkBtn,
  List,
  NavItem,
  NavLink,
  NavList,
  Wrapper,
} from './wrapper';

type IsActiveFilter = 'exact' | 'startsWith';

export interface PagePartial {
  title: string;
  description: string;
  url: string;
  isActiveFilter?: IsActiveFilter; //defaults to startsWith
  notificationMaxCount?: number;
  availableOnMarket: ('NO' | 'SE' | 'US')[];
  pages?: PagePartial[];
  icon?: React.ReactNode;
  hidden?: boolean;
}

export interface Page extends PagePartial {
  isActive: boolean;
  key: string;
  notificationCount?: number;
  pages?: Page[];
}

export interface MainNavigationBranchProps {
  title: string;
  displayName: string;
  avatar?: MainNAvigationAvatarProps;
  isActive?: boolean;
  id: string;
}

interface MainNAvigationAvatarProps {
  src: string;
  srcSet?: string;
  alt: string;
}

export interface MainNavigationFooterLinkProps {
  path: string;
  title: string;
  displayName: string;
  isActive?: boolean;
}

interface NavigationTreeItemProps {
  page: Page;
}

const NavigationTreeItem = ({ page }: NavigationTreeItemProps) => {
  const [collapsed, setCollapsed] = useState(false);
  const onClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (page.isActive) {
        e.preventDefault();
        setCollapsed(!collapsed);
      }
    },
    [collapsed, page],
  );
  const wasActive = useRef<boolean>(false);

  useEffect(() => {
    if (page.isActive && !wasActive.current) {
      setCollapsed(false);
    }
    wasActive.current = page.isActive;
  }, [page.isActive]);

  return (
    <Group $isActive={page.isActive}>
      <GroupBtn to={page.url} title={page.title}>
        <GroupBtnIcon aria-hidden="true" onClick={onClick}>
          {page.isActive && !collapsed ? (
            <IconChevronDown size="10px" />
          ) : (
            <IconChevronRight size="10px" />
          )}
        </GroupBtnIcon>
        {page.title}
        {!!page.notificationCount && (
          <Notification disablePointerEvents>{page.notificationCount}</Notification>
        )}
      </GroupBtn>
      {page.isActive && !collapsed && (
        <GroupList>
          {page.pages
            ?.filter((x) => !x.hidden)
            ?.map((subPage, i) => (
              <GroupItem key={`${subPage.url}-${i}`}>
                <GroupLink to={subPage.url} title={subPage.title} $isActive={subPage.isActive}>
                  {subPage.title}
                  {!!subPage.notificationCount && (
                    <Notification disablePointerEvents>{subPage.notificationCount}</Notification>
                  )}
                </GroupLink>
              </GroupItem>
            ))}
        </GroupList>
      )}
    </Group>
  );
};

interface NavigationTreeProps {
  navigationTree: Page[];
}

const NavigationTree = ({ navigationTree }: NavigationTreeProps) => (
  <NavList>
    {navigationTree
      .filter((x) => !x.hidden)
      .map((page) => (
        <NavItem key={page.title}>
          {page.pages?.length ? (
            <NavigationTreeItem page={page} />
          ) : (
            <NavLink to={page.url} title={page.title} $isActive={page.isActive}>
              {page.title}
              {!!page.notificationCount && (
                <Notification disablePointerEvents>{page.notificationCount || 18}</Notification>
              )}
            </NavLink>
          )}
        </NavItem>
      ))}
  </NavList>
);

interface MainNavigationProps {
  navigationTree: Page[];
  onSignOut: () => void;
}

const MainNavigation = ({ navigationTree, onSignOut }: MainNavigationProps) => (
  <Wrapper>
    <MarketLogo />
    <Body>
      <NavigationTree navigationTree={navigationTree} />
    </Body>
    <Footer>
      <List>
        <Item>
          <LinkBtn onClick={onSignOut} title={'Sign out'}>
            Sign out
          </LinkBtn>
        </Item>
      </List>
    </Footer>
  </Wrapper>
);

export default MainNavigation;
